import React, { useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { signOut } from 'aws-amplify/auth';
import {
    IconButton,
    Avatar,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Box,
    Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import LogoutIcon from '@mui/icons-material/Logout';
import PaletteIcon from '@mui/icons-material/Palette';

import { usernameAtom } from 'atoms/authAtoms';
import { themeAtom, type ThemeName } from 'atoms/themeAtom';
import { useSnackbar } from 'utils/hooks/useSnackbar';

// 表示ラベルは実際の配色に合わせた名称。内部 id（'white'|'black'|'red'）は localStorage 互換・
// 型・テスト維持のため据え置き、ラベルのみ実色に対応させる（white=紫 / black=ダーク+シアン / red=ワイン赤）。
const THEME_OPTIONS: { value: ThemeName; label: string }[] = [
    { value: 'gray', label: 'Gray' },
    { value: 'white', label: 'Purple' },
    { value: 'black', label: 'Midnight' },
    { value: 'red', label: 'Crimson' },
];

// アバター起点のメニュー。ユーザー名表示・テーマ選択・ログアウトを集約する。
export const UserMenu: React.FC = () => {
    const username = useAtomValue(usernameAtom);
    const [themeName, setThemeName] = useAtom(themeAtom);
    const { showError } = useSnackbar();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [signingOut, setSigningOut] = useState(false);

    // 未取得（E2E バイパス等）は「ゲスト」表示でフォールバックし落ちない。
    const displayName = username || 'ゲスト';
    const initial = displayName.charAt(0).toUpperCase();

    const handleSignOut = async () => {
        setSigningOut(true);
        try {
            await signOut();
        } catch {
            // 失敗時はユーザー向け文言を Snackbar で提示（console 出力では握りつぶさない）。
            showError('ログアウトに失敗しました。時間をおいて再度お試しください。');
        } finally {
            setSigningOut(false);
            setAnchorEl(null);
        }
    };

    return (
        <>
            <IconButton
                onClick={(e) => setAnchorEl(e.currentTarget)}
                color="inherit"
                aria-label="ユーザーメニュー"
                title={displayName}
                sx={{ ml: 0.5, flexShrink: 0 }}
            >
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>{initial}</Avatar>
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Box sx={{ px: 2, py: 1 }}>
                    <Typography variant="caption" color="text.secondary">ログイン中</Typography>
                    <Typography variant="subtitle2" noWrap>{displayName}</Typography>
                </Box>
                <Divider />
                <Box sx={{ px: 2, pt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PaletteIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    <Typography variant="overline" color="text.secondary">テーマ</Typography>
                </Box>
                {THEME_OPTIONS.map((opt) => (
                    <MenuItem
                        key={opt.value}
                        selected={themeName === opt.value}
                        onClick={() => setThemeName(opt.value)}
                    >
                        <ListItemIcon>
                            {themeName === opt.value ? <CheckIcon fontSize="small" /> : null}
                        </ListItemIcon>
                        <ListItemText primary={opt.label} />
                    </MenuItem>
                ))}
                <Divider />
                <MenuItem onClick={handleSignOut} disabled={signingOut}>
                    <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="ログアウト" />
                </MenuItem>
            </Menu>
        </>
    );
};
